import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { companyNameValidation, nameValidation, requiredValidation, titleValidation } from 'validators';
import { AnimatedTextField, Button, MenuItem, Select, TextField, Typography } from 'seagull';
import { CheckboxWithLabel } from 'seagull/components/MultiSelectFilterPicker/CheckboxWithLabel';
import { Close12 } from 'seagull/icons';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { BusinessIndustryType } from '~/store/store.model';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { StepTemplate } from '../StepTemplate';
import { useQboInVob } from '../useQboInVob';
import { Step } from '../VendorOnboardingModal.utils';
import styles from './FirstStep.module.scss';
const numOfInvoicesOptions = [
    {
        label: 'Less than 10',
        value: '0'
    },
    {
        label: '10-50',
        value: '10'
    },
    {
        label: '50-250',
        value: '50'
    },
    {
        label: '250-1000',
        value: '250'
    },
    {
        label: 'More than 1000',
        value: '1000'
    }
];
const businessIndustryTypesOptions = [
    BusinessIndustryType.Bookkeeping,
    BusinessIndustryType.Accounting,
    BusinessIndustryType.TaxPreparation,
    BusinessIndustryType.TaxRepresentation,
    BusinessIndustryType.TaxResolution,
    BusinessIndustryType.Marketing,
    BusinessIndustryType.Other
];
const businessIndustryTypeToLabel = {
    [BusinessIndustryType.Bookkeeping]: 'Bookkeeping',
    [BusinessIndustryType.Accounting]: 'Accounting',
    [BusinessIndustryType.TaxPreparation]: 'Tax Preparation',
    [BusinessIndustryType.TaxRepresentation]: 'Tax Representation',
    [BusinessIndustryType.TaxResolution]: 'Tax Resolution',
    [BusinessIndustryType.Marketing]: 'Marketing',
    [BusinessIndustryType.Other]: 'Other'
};
const firstNameValidator = nameValidation(undefined, {
    required: 'Please enter a first name'
});
const lastNameValidator = nameValidation(undefined, {
    required: 'Please enter a last name'
});
const companyNameValidator = companyNameValidation(undefined, {
    required: 'Please enter a company name'
});
const businessIndustryTypeValidator = {
    validate: (val)=>val.length > 0 || 'Please select an industry'
};
const numOfInvoicesValidator = requiredValidation('Please enter number of invoices');
const BUSINESS_DESCRIPTION_SUGGESTIONS = [
    'Bookkeeping firm',
    'Tax advisory',
    'Marketing agency',
    'Consulting services',
    'Accounting practice'
];
const Section = (props)=>/*#__PURE__*/ _jsxs("div", {
        className: styles.section,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: props.title
            }),
            props.children
        ]
    });
export function FirstStep(props) {
    const { initialState, onSubmit } = props;
    const { control, handleSubmit, watch, setValue, trigger, getFieldState } = useForm({
        mode: 'onChange',
        defaultValues: initialState
    });
    const { BUSINESS_DESCRIPTION_ONBOARDING } = useFeatureFlags();
    const watchBusinessTypes = watch('businessTypes');
    const [showNameSection, setShowNameSection] = useState(true);
    const { showQboStep } = useQboInVob();
    useOnMount(()=>{
        trigger([
            'name'
        ]).then(()=>{
            setShowNameSection(getFieldState('name').invalid);
        });
    });
    return /*#__PURE__*/ _jsx("form", {
        onSubmit: handleSubmit(onSubmit),
        children: /*#__PURE__*/ _jsx(StepTemplate, {
            currentStep: Step.First,
            stepper: "Step 1 of ".concat(showQboStep ? '3' : '2'),
            header: "Let's get you started",
            subHeader: "Tell us about your business so we can customize your account to fit your needs",
            footer: /*#__PURE__*/ _jsx(Button, {
                testId: "vob-step-".concat(Step.First, "-submit"),
                type: "submit",
                className: styles.submitBtn,
                children: "Next"
            }),
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.root,
                children: [
                    showNameSection && /*#__PURE__*/ _jsxs("div", {
                        className: styles.nameSection,
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "name.first",
                                rules: firstNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false
                                    }, connectControllerToTextField(renderProps)), {
                                        testId: "vob-first-name",
                                        placeholder: "First name"
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "name.last",
                                rules: lastNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false
                                    }, connectControllerToTextField(renderProps)), {
                                        testId: "vob-last-name",
                                        placeholder: "Last name"
                                    }))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "companyName",
                        rules: companyNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                collapseHelperText: false
                            }, connectControllerToTextField(renderProps)), {
                                testId: "vob-company-name",
                                placeholder: "Company name"
                            }))
                    }),
                    BUSINESS_DESCRIPTION_ONBOARDING ? /*#__PURE__*/ _jsx(Section, {
                        title: "How would you describe your business?",
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "businessDescription",
                            rules: {
                                required: 'Please describe your business'
                            },
                            render: (renderProps)=>{
                                const connected = connectControllerToTextField(renderProps);
                                return /*#__PURE__*/ _jsx(AnimatedTextField, _object_spread_props(_object_spread({}, connected), {
                                    suggestions: BUSINESS_DESCRIPTION_SUGGESTIONS,
                                    staticPrefix: "E.g. ",
                                    collapseHelperText: true,
                                    showHelperTextOnFocusOnly: true,
                                    helperText: connected.helperText || "We'll use this to select the right templates for your account",
                                    testId: "vob-business-description"
                                }));
                            }
                        })
                    }) : /*#__PURE__*/ _jsx(Section, {
                        title: "What services do you provide?",
                        children: watchBusinessTypes.includes(BusinessIndustryType.Other) && watchBusinessTypes.length === 1 ? /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "businessTypeOther",
                            rules: titleValidation('Service'),
                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                    collapseHelperText: false
                                }, connectControllerToTextField(renderProps)), {
                                    testId: "vob-business-type-other",
                                    placeholder: "Write your own (optional)",
                                    autoFocus: true,
                                    suffix: /*#__PURE__*/ _jsx("span", {
                                        className: styles.removeOther,
                                        onClick: ()=>{
                                            setValue('businessTypeOther', undefined);
                                            setValue('businessTypes', []);
                                        },
                                        "data-testid": "vob-business-type-other-remove",
                                        children: /*#__PURE__*/ _jsx(Close12, {})
                                    })
                                }))
                        }, "other") : /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "businessTypes",
                            rules: businessIndustryTypeValidator,
                            render: (renderProps)=>{
                                const _connectControllerToTextField = connectControllerToTextField(renderProps), { value, onChange } = _connectControllerToTextField, rest = _object_without_properties(_connectControllerToTextField, [
                                    "value",
                                    "onChange"
                                ]);
                                const displayValue = value.map((businessIndustryType)=>businessIndustryTypeToLabel[businessIndustryType]).join(', ');
                                return /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                    collapseHelperText: false
                                }, rest), {
                                    testId: "vob-business-type",
                                    placeholder: "Select",
                                    value: value,
                                    SelectProps: {
                                        multiple: true
                                    },
                                    displayValue: displayValue,
                                    children: businessIndustryTypesOptions.map((optionValue)=>/*#__PURE__*/ _jsx(CheckboxWithLabel, {
                                            label: businessIndustryTypeToLabel[optionValue],
                                            checked: value.includes(optionValue),
                                            onChange: (e)=>{
                                                onChange(e.target.checked ? value.concat([
                                                    optionValue
                                                ]) : value.filter((item)=>item !== optionValue));
                                            },
                                            className: styles.businessTypeOption,
                                            testId: "bt-option-".concat(optionValue)
                                        }, optionValue))
                                }));
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(Section, {
                        title: "How many invoices do you issue a month?",
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "numOfInvoices",
                            rules: numOfInvoicesValidator,
                            render: (renderProps)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                    collapseHelperText: false
                                }, connectControllerToTextField(renderProps)), {
                                    testId: "vob-num-of-invoices",
                                    placeholder: "Select",
                                    children: numOfInvoicesOptions.map((param)=>{
                                        let { label, value } = param;
                                        return /*#__PURE__*/ _jsx(MenuItem, {
                                            value: value,
                                            testId: "noi-option-".concat(value),
                                            children: label
                                        }, value);
                                    })
                                }))
                        })
                    })
                ]
            })
        })
    });
}
